import clsx from "clsx";

import { spansMultipleYears } from "~/helpers/formatDate";
import { formatMetricOrDimensionName } from "~/pages/GoogleDashboardPage/utils/formatMetricOrDimensionName";

import type {
  GoogleAnalyticsPropertyRealtimeReport,
  GoogleAnalyticsPropertyReport,
} from "../../../../types/RenderableWidget.type";
import {
  formatDimensionValue,
  parseDimensionDate,
} from "../../utils/formatDimensionValue";
import { formatMetricValue } from "../../utils/formatMetricValue";
import styles from "./TableView.module.scss";

export const TableView = ({
  data,
}: {
  data: GoogleAnalyticsPropertyReport | GoogleAnalyticsPropertyRealtimeReport;
}) => {
  // Hide the year on date columns when the entire range fits within a
  // single calendar year — same rule the chart axes use.
  const dateDimensionIdx =
    data.dimension_headers?.findIndex((h) => h.name === "date") ?? -1;
  const showYear =
    dateDimensionIdx === -1
      ? true
      : spansMultipleYears(
          (data.rows || [])
            .map((row) => {
              const value = row.dimension_values?.[dateDimensionIdx]?.value;

              return value ? parseDimensionDate(value) : null;
            })
            .filter((d): d is Date => d !== null),
        );

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {data.dimension_headers?.map((header) => (
            <th key={header.name}>
              {formatMetricOrDimensionName(header.name)}
            </th>
          ))}
          {data.metric_headers.map((header) => (
            <th
              key={header.name}
              className={clsx({
                [styles.alignRight]: header.type === "TYPE_INTEGER",
              })}
            >
              {formatMetricOrDimensionName(header.name)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.rows?.map((row, rowIdx) => (
          <tr key={rowIdx}>
            {row.dimension_values?.map(({ value }, idx) => {
              return (
                <td key={idx}>
                  {formatDimensionValue(
                    value,
                    data.dimension_headers![idx].name,
                    showYear,
                  )}
                </td>
              );
            })}
            {row.metric_values.map(({ value }, idx) => (
              <td
                className={clsx({
                  [styles.alignRight]:
                    data.metric_headers[idx].type === "TYPE_INTEGER",
                })}
                key={idx}
              >
                {formatMetricValue(value, data.metric_headers[idx].type)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
